import { Alert, Container, Skeleton, Typography } from "@firecms/ui";
import { useCallback, useMemo } from "react";

import { PAGE_SIZE } from "./PromoCodesList.constants";
import { type PromoCodesListProps } from "./PromoCodesList.types";
import { usePromoCodeDelete } from "./hooks/usePromoCodeDelete";
import { usePromoCodesList } from "./hooks/usePromoCodesList";
import PromoCodesTable from "./ui/PromoCodesTable";

const PromoCodesList: React.FC<PromoCodesListProps> = ({ preLoadedCodes, onDeleted }) => {
  const listParams = useMemo(
    () => ({
      limit: PAGE_SIZE,
      sort: "createdOnIsoDate" as const,
      sortDirection: "desc" as const,
    }),
    [],
  );

  const { data, loading, error, refetching, fetchMore, loadingMore } = usePromoCodesList({
    params: listParams,
    enabled: !preLoadedCodes?.promoCodes.length,
  });

  const handleDeleted = useCallback((): void => {
    onDeleted?.();
  }, [onDeleted]);

  const { deletePromoCode, loading: deleteLoading, error: deleteError } = usePromoCodeDelete(handleDeleted);
  const activeError = error ?? deleteError;

  if (loading && !preLoadedCodes) {
    return (
      <Container className="flex flex-col gap-3 p-6">
        {Array.from({ length: 12 }).map((_, index) => (
          <Skeleton key={`promo-codes-loading-skeleton-${String(index)}`} height={30} />
        ))}
      </Container>
    );
  }

  if (activeError) {
    return (
      <Container className="p-6">
        <Alert color="error">
          {deleteError ? "Fehler beim Löschen der Promo Codes" : "Fehler beim Laden der Promo Codes"}
          {": "}
          {activeError.message}
        </Alert>
      </Container>
    );
  }

  if (preLoadedCodes) {
    return (
      <Container className="flex flex-col gap-4 p-6">
        <Typography variant="h4">Promo Codes</Typography>
        <PromoCodesTable
          codes={preLoadedCodes.promoCodes}
          total={preLoadedCodes.total}
          loading={false}
          deleteLoading={deleteLoading}
          onDeletePromoCode={deletePromoCode}
        />
      </Container>
    );
  }

  return (
    <Container className="flex flex-col gap-4 p-6">
      <Typography variant="h4">Promo Codes</Typography>
      <Typography variant="body2" className="text-gray-600 dark:text-gray-300">
        {data?.total ?? 0} Codes insgesamt{refetching ? " (wird aktualisiert...)" : ""}
      </Typography>
      {data ? (
        <PromoCodesTable
          codes={data.promoCodes}
          total={data.total}
          onFetchMore={() => {
            void fetchMore();
          }}
          onDeletePromoCode={deletePromoCode}
          loading={false}
          loadingMore={loadingMore}
          deleteLoading={deleteLoading}
        />
      ) : null}
    </Container>
  );
};

export default PromoCodesList;
